import React from 'react';
import { Link, StyleSheet } from '@react-pdf/renderer';
import { startCase } from 'lodash';
import { Text, View, Field, Table, Page } from '../../components';
import { Theme } from '../../../../theme';
import {
  formatCurrencyOnBlur,
  parseDateValue,
} from '../../../../helpers/utils';

interface DataProps {
  value?: any;
  children?: React.ReactNode;
}

const Data = ({ value, children }: DataProps) => {
  if (value) {
    if (children) {
      return <Text>{children}</Text>;
    }
    return <Text>{value}</Text>;
  }
  return <Text>-</Text>;
};

const actionStyle = StyleSheet.create({
  section: { marginTop: 30 },
});

interface RowProps {
  noBorder?: boolean;
  style?: any;
  children?: React.ReactNode;
  [key: string]: any;
}

const Row = ({ noBorder, style, children, ...rest }: RowProps) => (
  <View
    style={[
      {
        flexDirection: 'row',
        ...(noBorder
          ? {}
          : { borderBottom: `1pt solid ${Theme.colors.greyLight}` }),
      },
      style,
    ]}
    {...rest}
  >
    {children}
  </View>
);

interface ColumnProps {
  noBorder?: boolean;
  style?: any;
  children?: React.ReactNode;
  [key: string]: any;
}

const Column = ({ noBorder, style, children, ...rest }: ColumnProps) => (
  <View
    style={[
      {
        flexGrow: 1,
        ...(noBorder
          ? {}
          : { borderRight: `1pt solid ${Theme.colors.greyLight}` }),
      },
      style,
    ]}
    {...rest}
  >
    {children}
  </View>
);

interface CellProps {
  children?: React.ReactNode;
  header?: string;
}

const Cell = ({ children, header }: CellProps) => (
  <View>
    <View
      backgroundColor={Theme.colors.greyLighter}
      borderBottom={`1pt solid ${Theme.colors.primary}`}
      mb={1}
      p="4pt"
      width="auto"
    >
      <Text>{header}</Text>
    </View>
    <View p="5pt">{children}</View>
  </View>
);

interface Props {
  data: any;
  meta: any;
}

const OpportunityReview = ({ data: project, meta }: Props) => {
  const {
    description,
    previousFunding,
    deadline,
    expected_response,
    deadline_type,
    submission_link,
    submission_username,
    submission_password,
    submission_notes,
    award_start,
    fundedProgram,
    estimated_hours,
    funder,
    actions,
    client,
    contacts,
  } = project;

  const LOIs =
    actions.length > 0 ? actions.filter((action) => action.type === 'LOI') : [];

  const LOIDeadline =
    LOIs.length > 0
      ? LOIs.reduce((acc, action) => {
          const aDate = parseDateValue(action.deadline).toDate();
          const bDate = parseDateValue(acc.deadline).toDate();
          return aDate > bDate ? action : acc;
        }).deadline
      : null;

  return (
    <>
      <Page meta={meta}>
        <View
          fontSize="10pt"
          flexGrow={1}
          border={`1pt solid ${Theme.colors.greyLight}`}
          mb="25pt"
        >
          <Row>
            <Column>
              <Cell header="Client">
                <Data value={client.name} />
              </Cell>
            </Column>
            <Column noBorder>
              <Cell header="Project">
                <Data value={description} />
              </Cell>
            </Column>
          </Row>
          <Row>
            <Column width="50%">
              <Cell header="Submission">
                <Field
                  label="Link"
                  value={
                    <Data value={submission_link}>
                      <Link href={submission_link}>{submission_link}</Link>
                    </Data>
                  }
                  labelProps={{ width: 65 }}
                />
                <Field
                  label="Username"
                  value={<Data value={submission_username} />}
                  labelProps={{ width: 65 }}
                />
                <Field
                  label="PW"
                  value={<Data value={submission_password} />}
                  labelProps={{ width: 65 }}
                />
              </Cell>
            </Column>
            <Column width="50%" noBorder>
              <Cell header="Funder">
                <Field label="Name" value={<Data value={funder?.name} />} />
                <Field
                  label="Link"
                  value={
                    <Data value={funder?.website}>
                      <Link href={funder?.website}>{funder?.website}</Link>
                    </Data>
                  }
                />
                <Field label="Contact" value={<Data value={funder?.phone} />} />
              </Cell>
            </Column>
          </Row>

          <Row>
            <Column noBorder>
              <Cell header="Submission Notes">
                <Data value={submission_notes} />
              </Cell>
            </Column>
          </Row>

          <Row>
            <Column width="25%">
              <Cell header="Previous Funding Rec'd">
                <Data
                  value={formatCurrencyOnBlur(previousFunding.toString())}
                />
              </Cell>
            </Column>
            <Column width="25%">
              <Cell header="LOI Due Date">
                <Text>
                  <Data value={LOIDeadline}>
                    {parseDateValue(LOIDeadline).format('YYYY-MM-DD')}
                  </Data>
                </Text>
              </Cell>
            </Column>
            <Column width="25%">
              <Cell header="Application Due Date">
                <Data value={deadline}>
                  {parseDateValue(deadline).format('YYYY-MM-DD')}
                </Data>
              </Cell>
            </Column>
            <Column width="25%" noBorder>
              <Cell header="Expected Response Date">
                <Data value={expected_response}>
                  {parseDateValue(expected_response).format('YYYY-MM-DD')}
                </Data>
              </Cell>
            </Column>
          </Row>

          <Row>
            <Column width="25%">
              <Cell header="Deadline Frequency">
                <Data value={deadline_type}>{startCase(deadline_type)}</Data>
              </Cell>
            </Column>
            <Column width="25%">
              <Cell header="Project Begin Date">
                <Data value={award_start}>
                  {parseDateValue(award_start).format('YYYY-MM-DD')}
                </Data>
              </Cell>
            </Column>
            <Column width="25%">
              <Cell header="Program to be Funded">
                <Data value={fundedProgram?.name} />
              </Cell>
            </Column>
            <Column width="25%" noBorder>
              <Cell header="Est Hours for Preparation">
                <Data value={estimated_hours} />
              </Cell>
            </Column>
          </Row>

          <Row noBorder>
            <Column noBorder>
              <Cell header="Team">
                <Table
                  data={contacts}
                  columns={[
                    {
                      Header: 'Name',
                      accessor: 'full_name',
                      width: 250,
                    },
                    {
                      Header: 'Email',
                      accessor: 'email',
                      width: 285,
                    },
                    {
                      Header: 'Phone',
                      accessor: 'phone',
                      width: 250,
                    },
                  ]}
                />
              </Cell>
            </Column>
          </Row>
        </View>
      </Page>
      <Page meta={meta}>
        <Row noBorder>
          <Column noBorder style={actionStyle.section}>
            <Cell header="Action Items">
              <Table
                data={actions}
                columns={[
                  {
                    Header: 'Title',
                    accessor: 'title',
                    width: 200,
                  },
                  {
                    Header: 'Status',
                    accessor: 'status',
                    width: 135,
                  },
                  {
                    Header: 'Type',
                    accessor: 'type',
                  },
                  {
                    Header: 'Deadline',
                    accessor: 'deadline',
                    width: 150,
                  },
                  {
                    Header: 'Assignee',
                    accessor: 'name',
                    width: 200,
                  },
                ]}
              />
            </Cell>
          </Column>
        </Row>
      </Page>
    </>
  );
};

export default OpportunityReview;
